import SectionHeader from "@/components/feature/panel/SectionHeader";
import EmptyState from "@/components/feature/panel/EmptyState";
import PanelLinks from "@/components/feature/panel/PanelLinks";
import { getDataClient } from "@/lib/dataClient";
import { useDataQuery } from "@/hooks/useDataQuery";

interface CourseRow {
  id: string;
  name: string;
  teacher_name: string | null;
  total_topics: number;
}

interface VideoRow {
  id: string;
  title: string;
  subject: string;
  teacher_name: string | null;
  duration_minutes: number | null;
}

interface ContentData {
  courses: CourseRow[];
  videos: VideoRow[];
}

export default function AdminIcerikler() {
  const { data, loading, error, reload } = useDataQuery<ContentData>(async () => {
    const dataClient = getDataClient();
    const [cRes, vRes] = await Promise.all([
      dataClient.from("courses").select("id, name, teacher_name, total_topics").order("sort_order"),
      dataClient.from("video_lessons").select("id, title, subject, teacher_name, duration_minutes").order("sort_order"),
    ]);
    if (cRes.error) throw cRes.error;
    if (vRes.error) throw vRes.error;
    return {
      courses: (cRes.data ?? []) as CourseRow[],
      videos: (vRes.data ?? []) as VideoRow[],
    };
  });

  const total = (data?.courses.length ?? 0) + (data?.videos.length ?? 0);

  return (
    <div className="space-y-6">
      <SectionHeader
        title="İçerik Yönetimi"
        description="Ders ve video kayıtlarını görüntüle, takip et."
      />

      {loading ? (
        <EmptyState icon="ri-loader-4-line" title="Yükleniyor..." />
      ) : error ? (
        <div className="rounded-lg border border-background-200 bg-background-50 p-6 text-center">
          <p className="text-sm text-foreground-600">{error}</p>
          <button
            type="button"
            onClick={reload}
            className="mt-4 inline-flex items-center gap-2 whitespace-nowrap px-4 py-2 rounded-md text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
          >
            <i className="ri-refresh-line" aria-hidden="true"></i>
            Tekrar dene
          </button>
        </div>
      ) : total === 0 ? (
        <EmptyState
          icon="ri-stack-line"
          title="Henüz içerik kaydı yok"
          description="Öğretmenler ders ve video ekledikçe burada listelenecek."
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <section className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading font-semibold text-base text-foreground-950">Dersler</h2>
            {data && data.courses.length > 0 ? (
              <ul className="mt-3 divide-y divide-background-200">
                {data.courses.map((c) => (
                  <li key={c.id} className="py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground-900">{c.name}</p>
                      <p className="text-xs text-foreground-500">{c.teacher_name ?? "—"}</p>
                    </div>
                    <span className="text-xs text-foreground-500 whitespace-nowrap">{c.total_topics} konu</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-3 text-sm text-foreground-500">Kayıtlı ders yok.</p>
            )}
          </section>

          <section className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading font-semibold text-base text-foreground-950">Video Dersler</h2>
            {data && data.videos.length > 0 ? (
              <ul className="mt-3 divide-y divide-background-200">
                {data.videos.map((v) => (
                  <li key={v.id} className="py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground-900">{v.title}</p>
                      <p className="text-xs text-foreground-500">{v.subject} · {v.teacher_name ?? "—"}</p>
                    </div>
                    <span className="text-xs text-foreground-500 whitespace-nowrap">
                      {v.duration_minutes != null ? `${v.duration_minutes} dk` : "—"}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-3 text-sm text-foreground-500">Kayıtlı video yok.</p>
            )}
          </section>
        </div>
      )}

      <PanelLinks
        links={[
          { label: "Canlı Dersler", href: "/yonetici/canli-dersler", icon: "ri-vidicon-line" },
          { label: "Sınavlar", href: "/yonetici/sinavlar", icon: "ri-file-list-3-line" },
        ]}
      />
    </div>
  );
}